import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  administrativeBoundariesApi,
  type AdministrativeBoundarySummary,
  type AdministrativeBoundary,
} from '../../spatial';
import './administrative-boundary-selector.css';

/**
 * Props para el selector de límites administrativos
 */
export interface AdministrativeBoundarySelectorProps {
  /**
   * País del cual seleccionar límites administrativos
   * @default 'argentina'
   */
  country?: 'argentina';

  /**
   * Límite administrativo seleccionado actualmente
   */
  selectedBoundary: AdministrativeBoundary | null;

  /**
   * Callback cuando se selecciona un límite administrativo
   * @param boundary - Límite administrativo seleccionado con geometría completa
   */
  onBoundarySelect: (boundary: AdministrativeBoundary) => void;

  /**
   * Callback cuando se limpia la selección
   */
  onClear: () => void;
}

/**
 * AdministrativeBoundarySelector - Selector de límites administrativos
 *
 * @remarks
 * Componente dropdown con búsqueda tipo-ahead para seleccionar
 * límites administrativos (departamentos, municipios) desde el backend.
 *
 * **Funcionalidad:**
 * - Búsqueda incremental mientras el usuario escribe
 * - Carga de datos desde API al montar
 * - Obtención de geometría completa al seleccionar
 * - Estados de loading y error
 * - Limpieza de selección
 *
 * **Casos de uso:**
 * - Selección de área de proyecto por límite administrativo
 * - Alternativa al rectángulo dibujado manualmente
 *
 * @example
 * ```typescript
 * <AdministrativeBoundarySelector
 *   country="argentina"
 *   selectedBoundary={selectedBoundary}
 *   onBoundarySelect={handleBoundarySelect}
 *   onClear={handleClear}
 * />
 * ```
 *
 * @version 1.0
 * @date 2026-03-16
 */
export function AdministrativeBoundarySelector({
  country = 'argentina',
  selectedBoundary,
  onBoundarySelect,
  onClear,
}: AdministrativeBoundarySelectorProps): JSX.Element {
  const { t } = useTranslation('projects');

  // Estados
  const [searchTerm, setSearchTerm] = useState('');
  const [allBoundaries, setAllBoundaries] = useState<AdministrativeBoundarySummary[]>([]);
  const [filteredBoundaries, setFilteredBoundaries] = useState<AdministrativeBoundarySummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSelection, setIsLoadingSelection] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  // Ref para detectar clicks fuera del dropdown
  const dropdownRef = useRef<HTMLDivElement>(null);

  /**
   * Cargar todos los límites administrativos al montar el componente
   */
  useEffect(() => {
    loadBoundaries();
  }, [country]);

  /**
   * Filtrar límites cuando cambia el término de búsqueda
   */
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredBoundaries(allBoundaries);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = allBoundaries.filter(
      (boundary) =>
        boundary.name.toLowerCase().includes(term) ||
        boundary.fullName.toLowerCase().includes(term) ||
        boundary.code.toLowerCase().includes(term)
    );

    setFilteredBoundaries(filtered);
  }, [searchTerm, allBoundaries]);

  /**
   * Cerrar dropdown al hacer click fuera
   */
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Carga los límites administrativos desde la API
   */
  async function loadBoundaries() {
    setIsLoading(true);
    setError(null);

    try {
      if (country === 'argentina') {
        const boundaries = await administrativeBoundariesApi.getArgentinaDepartamentos();
        setAllBoundaries(boundaries);
        setFilteredBoundaries(boundaries);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Error cargando límites administrativos';
      setError(errorMsg);
      console.error('Error cargando límites:', err);
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * Maneja la selección de un límite administrativo
   * Carga la geometría completa desde la API
   */
  async function handleSelect(summary: AdministrativeBoundarySummary) {
    setIsLoadingSelection(true);
    setError(null);
    setShowDropdown(false);

    try {
      // Obtener geometría completa
      const fullBoundary = await administrativeBoundariesApi.getArgentinaDepartamentoById(
        summary.id
      );

      onBoundarySelect(fullBoundary);
      setSearchTerm(summary.fullName); // Mostrar nombre completo en el input
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Error cargando geometría';
      setError(errorMsg);
      console.error('Error cargando geometría:', err);
    } finally {
      setIsLoadingSelection(false);
    }
  }

  /**
   * Limpia la selección actual
   */
  function handleClear() {
    setSearchTerm('');
    onClear();
  }

  /**
   * Maneja el foco en el input
   */
  function handleFocus() {
    setShowDropdown(true);
  }

  /**
   * Maneja el cambio en el input de búsqueda
   */
  function handleSearchChange(value: string) {
    setSearchTerm(value);
    setShowDropdown(true);
  }

  return (
    <div className="administrative-boundary-selector" ref={dropdownRef}>
      {/* Input de búsqueda */}
      <div className="selector-input-wrapper">
        <input
          type="text"
          className="selector-input"
          placeholder={t('selectAdministrativeBoundary')}
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          onFocus={handleFocus}
          disabled={isLoading || isLoadingSelection}
        />

        {/* Botón limpiar */}
        {selectedBoundary && (
          <button
            type="button"
            className="selector-clear-btn"
            onClick={handleClear}
            disabled={isLoadingSelection}
            title={t('clearSelection')}
          >
            ✕
          </button>
        )}
      </div>

      {/* Estados de carga */}
      {isLoading && (
        <div className="selector-loading">
          {t('loadingBoundaries')}
        </div>
      )}

      {isLoadingSelection && (
        <div className="selector-loading">
          {t('loadingGeometry')}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="selector-error">
          {error}
        </div>
      )}

      {/* Dropdown de resultados */}
      {showDropdown && !isLoading && !isLoadingSelection && filteredBoundaries.length > 0 && (
        <div className="selector-dropdown">
          <div className="selector-results-header">
            {filteredBoundaries.length} {t('resultsFound')}
          </div>
          <ul className="selector-results-list">
            {filteredBoundaries.slice(0, 50).map((boundary) => (
              <li
                key={boundary.id}
                className="selector-result-item"
                onClick={() => handleSelect(boundary)}
              >
                <div className="result-name">{boundary.name}</div>
                <div className="result-full-name">{boundary.fullName}</div>
              </li>
            ))}
            {filteredBoundaries.length > 50 && (
              <li className="selector-result-item result-more">
                +{filteredBoundaries.length - 50} {t('moreResults')}
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Sin resultados */}
      {showDropdown && !isLoading && searchTerm && filteredBoundaries.length === 0 && (
        <div className="selector-no-results">
          {t('noResultsFound')}
        </div>
      )}

      {/* Información de selección actual */}
      {selectedBoundary && (
        <div className="selector-selection-info">
          <span className="selection-label">{t('selected')}:</span>
          <span className="selection-value">{selectedBoundary.fullName}</span>
        </div>
      )}
    </div>
  );
}
